/**
 * The option shape shared by `<Select>` and `<MultiSelect>` — reference §14.
 */
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Trailing muted node — a count, a badge, a level. */
  right?: React.ReactNode;
  /** Muted second line. */
  sublabel?: string;
  /**
   * Further muted LABELLED lines under the label, in order — `<Select>` only.
   *
   * For an option whose identity is a short name and whose meaning is prose the reader picks
   * on — a goal over its objective and its strategy. Each line WRAPS to two lines and then
   * ellipsizes, since a sentence cut at one line loses its end. An empty value is not drawn.
   * Displayed text is not searched; pass it in `keywords` as well where it should be.
   */
  details?: { label: string; value: string | null | undefined }[];
  /** Extra searchable text that is not displayed. `<Select>` only. */
  keywords?: string;
  /** Non-selectable separator row. */
  header?: boolean;
  /** Renders indented, for a tree child. */
  indent?: boolean;
}

export type SelectSort = 'asc' | 'none' | ((a: SelectOption, b: SelectOption) => number);

/**
 * Sorts options, leaving `header` rows anchored to the entries that follow them so a
 * grouped list does not scramble.
 */
export function sortOptions(options: SelectOption[], sort: SelectSort): SelectOption[] {
  if (sort === 'none') {
    return options;
  }

  const comparator =
    typeof sort === 'function' ? sort : (a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label);

  // Headers partition the list; each partition sorts independently.
  const groups: SelectOption[][] = [[]];

  for (const option of options) {
    if (option.header) {
      groups.push([option], []);
    } else {
      groups[groups.length - 1].push(option);
    }
  }

  return groups.flatMap((group) =>
    group.length === 1 && group[0].header ? group : [...group].sort(comparator),
  );
}
